import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { CaretDown, List, X, Minus, Plus, Handbag, Trash } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { BrandLogo } from "../brand/BrandLogo";
import { SHOP_CATS, ABOUT_TABS, PRODUCTS, SWATCHES, useCart } from "../../lib/site";

export const shopHref = (c) => (c === "all" ? "/shop" : `/shop?c=${c}`);
export const aboutHref = (t) => `/about?tab=${t}`;
const SHOP_ITEMS = [...SHOP_CATS.map(([k, l]) => [l, shopHref(k)]), ["Products with QR", "/products"]];
const ABOUT_ITEMS = ABOUT_TABS.map(([k, l]) => [l, aboutHref(k)]);
const ink = "text-[#16294A]";

const Drop = ({ label, items, active }) => (
  <div className="relative group">
    <button className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold ${active ? "text-[#D7263D] bg-white" : ink} hover:bg-white transition-colors`} data-testid={`nav-${label.toLowerCase()}-menu`}>
      {label}<CaretDown size={12} weight="bold" /></button>
    <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-50">
      <div className="bg-white rounded-2xl shadow-xl border border-slate-100 py-2 min-w-[220px]">
        {items.map(([l, h]) => <Link key={l} to={h} data-testid={`nav-${label.toLowerCase()}-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`block px-4 py-2 text-sm ${ink} hover:bg-[#F2F4F8]`}>{l}</Link>)}
      </div>
    </div>
  </div>
);

const MobileSec = ({ label, items, open, toggle, onClose }) => (<>
  <button onClick={toggle} data-testid={`mobile-menu-${label.toLowerCase()}`} className={`w-full flex justify-between items-center px-5 py-4 text-lg font-bold text-[#2B6CD9] ${open ? "bg-[#F2F4F8]" : ""}`}>
    {label}{open ? <Minus size={18} /> : <Plus size={18} />}</button>
  {open && items.map(([l, h]) => <Link key={l} to={h} onClick={onClose} className={`block px-5 py-3.5 font-semibold ${ink} border-b border-slate-100`}>{l}</Link>)}
</>);

function MobileMenu({ onClose }) {
  const [open, setOpen] = useState({ shop: true, about: false });
  useEffect(() => { document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = ""; }; }, []);
  return (
    <div className="fixed inset-0 z-[60] bg-white flex flex-col lg:hidden" data-testid="mobile-menu">
      <div className="flex items-center justify-between px-5 py-4"><BrandLogo size={30} />
        <button onClick={onClose} data-testid="mobile-menu-close" className="w-11 h-11 rounded-full bg-[#F2F4F8] flex items-center justify-center"><X size={20} /></button></div>
      <div className="flex-1 overflow-y-auto border-t border-slate-100">
        <MobileSec label="Shop" items={SHOP_ITEMS} open={open.shop} toggle={() => setOpen({ ...open, shop: !open.shop })} onClose={onClose} />
        <a href="/#groups" onClick={onClose} className="block px-5 py-4 text-lg font-bold text-[#2B6CD9]">For organisations</a>
        <MobileSec label="About" items={ABOUT_ITEMS} open={open.about} toggle={() => setOpen({ ...open, about: !open.about })} onClose={onClose} />
      </div>
      <div className="p-5 space-y-3 border-t border-slate-100">
        <Link to="/sign-up" data-testid="mobile-menu-get-id" className="block text-center rounded-full bg-[#D7263D] text-white font-bold py-3.5">Get your bsafe ID</Link>
        <Link to="/sign-in" data-testid="mobile-menu-sign-in" className={`block text-center rounded-full border border-slate-300 ${ink} font-bold py-3.5`}>Sign in</Link>
      </div>
    </div>
  );
}

function CartDrawer({ open, onOpenChange }) {
  const { items, setQty, count } = useCart();
  const { isSignedIn } = useAuth();
  const nav = useNavigate();
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-white text-[#16294A] border-slate-200 flex flex-col" data-testid="cart-drawer" style={{ fontFamily: "Sora, sans-serif" }}>
        <SheetHeader><SheetTitle className="text-[#16294A]">Your cart ({count})</SheetTitle></SheetHeader>
        <div className="flex-1 overflow-y-auto mt-4 space-y-3">
          {items.length === 0 && <div className="text-sm text-slate-500" data-testid="cart-empty">Your cart is empty.</div>}
          {items.map((i, k) => { const p = PRODUCTS.find((x) => x.id === i.id); return p && (
            <div key={k} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3" data-testid={`cart-line-${i.id}-${i.colour}`}>
              <span className="w-4 h-4 rounded-full shrink-0" style={{ background: SWATCHES[i.colour] }} />
              <div className="flex-1 min-w-0"><div className="text-sm font-bold truncate">{p.name}</div><div className="text-xs text-slate-500">{i.colour} · UGX [0]</div></div>
              <div className="flex items-center gap-2">
                <button className="w-7 h-7 rounded-full bg-[#F2F4F8]" data-testid={`cart-dec-${k}`} onClick={() => setQty(k, i.qty - 1)}>{i.qty === 1 ? <Trash size={12} className="mx-auto" /> : "−"}</button>
                <span className="text-sm w-4 text-center" data-testid={`cart-qty-${k}`}>{i.qty}</span>
                <button className="w-7 h-7 rounded-full bg-[#F2F4F8]" data-testid={`cart-inc-${k}`} onClick={() => setQty(k, i.qty + 1)}>+</button>
              </div>
            </div>); })}
        </div>
        <div className="border-t border-slate-200 pt-4 space-y-2">
          <div className="flex justify-between text-sm font-bold"><span>Total</span><span>Calculated at checkout</span></div>
          <p className="text-xs text-slate-500">Pay by mobile money or bank transfer. Checkout creates an invoice in your account; we start printing once it's paid.</p>
          <button disabled={!count} data-testid="cart-checkout-btn" onClick={() => { onOpenChange(false); nav(isSignedIn ? "/app/checkout" : "/sign-up"); }}
            className="w-full rounded-full bg-[#D7263D] disabled:opacity-40 text-white font-bold py-3">{isSignedIn ? "Checkout – get invoice" : "Create free profile to order"}</button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function SiteNav({ active = "" }) {
  const [menu, setMenu] = useState(false);
  const [cart, setCart] = useState(false);
  const { count } = useCart();
  const { isSignedIn } = useAuth();
  return (<>
    {active === "home" && <Link to="/sign-up" data-testid="announcement-bar" className="block bg-[#D7263D] text-white text-xs sm:text-sm text-center py-2 px-4">
      <span className="bg-white text-[#D7263D] rounded-full px-2 py-0.5 text-[10px] font-extrabold mr-2">FREE</span>
      Your bsafe profile is free. Get your own QR code and responder link today. <span className="underline font-semibold">Create free profile →</span></Link>}
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between gap-4">
        <Link to="/" data-testid="site-home-link"><BrandLogo size={32} testid="landing-logo" /></Link>
        <nav className="hidden lg:flex items-center bg-[#F2F4F8] rounded-full p-1">
          <Drop label="Shop" items={SHOP_ITEMS} active={active === "shop"} />
          <a href="/#groups" className={`px-4 py-2 rounded-full text-sm font-semibold ${ink} hover:bg-white transition-colors`} data-testid="nav-for-organisations">For organisations</a>
          <a href="/#how" className={`px-4 py-2 rounded-full text-sm font-semibold ${ink} hover:bg-white transition-colors`} data-testid="nav-how-it-works">How it works</a>
          <Drop label="About" items={ABOUT_ITEMS} active={active === "about"} />
        </nav>
        <div className="flex items-center gap-2">
          {isSignedIn ? <Link to="/app" data-testid="nav-my-account-btn" className="hidden sm:block px-5 py-2.5 rounded-full bg-[#16294A] text-white text-sm font-bold hover:bg-[#D7263D] transition-colors">My account</Link> : <>
          <Link to="/sign-in" data-testid="landing-sign-in-btn" className={`hidden sm:block px-3 py-2 text-sm font-semibold ${ink} hover:text-[#D7263D] transition-colors`}>Sign in</Link>
          <Link to="/sign-up" data-testid="landing-get-started-btn" className="hidden sm:block px-5 py-2.5 rounded-full bg-[#16294A] text-white text-sm font-bold hover:bg-[#D7263D] transition-colors">Create free profile</Link></>}
          <button onClick={() => setCart(true)} data-testid="cart-btn" className="relative w-10 h-10 rounded-full bg-[#F2F4F8] flex items-center justify-center text-[#16294A]">
            <Handbag size={19} />{count > 0 && <span data-testid="cart-count" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#D7263D] text-white text-[10px] font-bold flex items-center justify-center px-1">{count}</span>}</button>
          <button className="lg:hidden w-10 h-10 rounded-full bg-[#F2F4F8] flex items-center justify-center text-[#16294A]" onClick={() => setMenu(true)} data-testid="landing-mobile-menu-btn"><List size={20} /></button>
        </div>
      </div>
    </header>
    {menu && <MobileMenu onClose={() => setMenu(false)} />}
    <CartDrawer open={cart} onOpenChange={setCart} />
  </>);
}
